import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import SearchBar from '../../components/common/SearchBar';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import RichTextEditor from '../../components/common/RichTextEditor';
import MultiFileUpload from '../../components/common/MultiFileUpload';
import { useAuth } from '../../context/AuthContext';
import {
  getNoticeList, createNotice, updateNotice, deleteNotice,
  getNoticeAttachments, uploadNoticeAttachments, deleteNoticeAttachment, downloadNoticeAttachment,
} from '../../api/notice';
import { formatDateTime } from '../../utils/format';

export default function NoticeListPage() {
  const { auth } = useAuth();
  const [condition, setCondition] = useState({ pageNo: 1, pageSize: 10 });
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchList = useCallback(async (cond) => {
    setLoading(true);
    setError('');
    try {
      const res = await getNoticeList(cond);
      setPage(res.data);
    } catch (err) {
      setError(err.response?.data?.message || '공지사항 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(condition); }, [condition, fetchList]);

  const handleCreate = async (form, pendingFiles) => {
    try {
      const res = await createNotice({ ...form, createdBy: auth.adminName });
      const noticeId = res.data?.noticeId;
      if (noticeId && pendingFiles.length > 0) {
        await uploadNoticeAttachments(noticeId, pendingFiles, auth.adminName);
      }
      setCreating(false);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '공지사항 등록에 실패했습니다.');
    }
  };

  const handleUpdate = async (form, pendingFiles) => {
    try {
      await updateNotice(editing.noticeId, { ...form, updatedBy: auth.adminName });
      if (pendingFiles.length > 0) {
        await uploadNoticeAttachments(editing.noticeId, pendingFiles, auth.adminName);
      }
      setEditing(null);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '공지사항 수정에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteNotice(deleteTarget.noticeId);
      setDeleteTarget(null);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '공지사항 삭제에 실패했습니다.');
    }
  };

  return (
    <AdminLayout title="공지사항관리" menuUrl="/admin/notice">
      {/* 타이틀 영역 */}
      <div className="page-header">
        <div>
          <h1>공지사항관리</h1>
          <div className="subtitle">공지사항을 등록, 수정, 삭제합니다.</div>
        </div>
      </div>

      {/* 헤더 영역: 검색(서버) + 건수 / 등록 액션 */}
      <div className="list-toolbar">
        <SearchBar placeholder="제목으로 검색" useDateRange onSearch={(cond) => setCondition((c) => ({ ...c, ...cond }))} />
        <div className="list-toolbar-right">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ 공지 등록</button>
        </div>
      </div>

      <div className="list-count-bar">
        <span className="list-count">전체 <b>{page?.totalCount ?? 0}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>제목</th><th>고정</th><th>노출</th><th>조회수</th><th>등록일</th><th>관리</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={6} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && page?.list?.length === 0 && <tr><td colSpan={6} className="empty-row">등록된 공지사항이 없습니다.</td></tr>}
              {!loading && !error && page?.list?.map((n) => (
                <tr key={n.noticeId}>
                  <td>{n.title}</td>
                  <td>{n.fixTopYn === 'Y' ? <StatusBadge value="Y" label="고정" /> : '-'}</td>
                  <td><StatusBadge value={n.useYn} label={n.useYn === 'Y' ? '노출' : '숨김'} /></td>
                  <td>{n.viewCount}</td>
                  <td>{formatDateTime(n.createdAt)}</td>
                  <td className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(n)}>수정</button>
                    <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(n)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {page && <Pagination {...page} onChange={(pageNo) => setCondition((c) => ({ ...c, pageNo }))} />}

      {creating && <NoticeFormModal onClose={() => setCreating(false)} onSave={handleCreate} />}
      {editing && <NoticeFormModal notice={editing} onClose={() => setEditing(null)} onSave={handleUpdate} />}
      {deleteTarget && (
        <ConfirmDialog title="공지사항 삭제" message={`'${deleteTarget.title}' 공지사항을 삭제하시겠습니까? 첨부파일도 함께 삭제해야 할 수 있습니다.`}
          confirmLabel="삭제" danger onCancel={() => setDeleteTarget(null)} onConfirm={handleDelete} />
      )}
    </AdminLayout>
  );
}

function NoticeFormModal({ notice, onClose, onSave }) {
  const isNew = !notice;
  const [form, setForm] = useState({
    title: notice?.title || '',
    content: notice?.content || '',
    useYn: notice?.useYn || 'Y',
    fixTopYn: notice?.fixTopYn || 'N',
  });
  const [saving, setSaving] = useState(false);
  const [contentError, setContentError] = useState('');

  // 새로 추가할(아직 업로드 안 한) 파일들
  const [pendingFiles, setPendingFiles] = useState([]);
  // 수정 모드에서 이미 서버에 저장된 첨부파일 목록
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachError, setAttachError] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);

  const fetchAttachments = useCallback(async () => {
    if (!notice?.noticeId) return;
    setLoadingAttachments(true);
    setAttachError('');
    try {
      const res = await getNoticeAttachments(notice.noticeId);
      setExistingAttachments(res.data || []);
    } catch (err) {
      setAttachError(err.response?.data?.message || '첨부파일 목록을 불러오지 못했습니다.');
    } finally {
      setLoadingAttachments(false);
    }
  }, [notice?.noticeId]);

  useEffect(() => { fetchAttachments(); }, [fetchAttachments]);

  const isContentEmpty = (html) => !html || !html.replace(/<[^>]*>/g, '').trim();

  const submit = async (e) => {
    e.preventDefault();
    if (isContentEmpty(form.content)) {
      setContentError('내용을 입력하세요.');
      return;
    }
    setContentError('');
    setSaving(true);
    try {
      await onSave(form, pendingFiles);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveExisting = async () => {
    try {
      await deleteNoticeAttachment(removeTarget.attachId);
      setRemoveTarget(null);
      fetchAttachments();
    } catch (err) {
      window.alert(err.response?.data?.message || '첨부파일 삭제에 실패했습니다.');
    }
  };

  return (
    <Modal title={isNew ? '공지사항 등록' : '공지사항 수정'} onClose={onClose} width={640}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}>
      <form onSubmit={submit}>
        <div className="field">
          <label>제목</label>
          <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>

        <div className="field">
          <label>내용</label>
          <RichTextEditor
            value={form.content}
            onChange={(html) => { setForm({ ...form, content: html }); if (contentError) setContentError(''); }}
            placeholder="공지 내용을 입력하세요"
          />
          {contentError && <div className="text-danger" style={{ fontSize: 12.5, marginTop: 6 }}>{contentError}</div>}
        </div>

        <div className="field-row">
          <div className="field checkbox-row">
            <input type="checkbox" id="fixTopYn" checked={form.fixTopYn === 'Y'} onChange={(e) => setForm({ ...form, fixTopYn: e.target.checked ? 'Y' : 'N' })} />
            <label htmlFor="fixTopYn" style={{ margin: 0 }}>상단 고정</label>
          </div>
          <div className="field checkbox-row">
            <input type="checkbox" id="noticeUseYn" checked={form.useYn === 'Y'} onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })} />
            <label htmlFor="noticeUseYn" style={{ margin: 0 }}>노출</label>
          </div>
        </div>

        <div className="field">
          <label>첨부파일</label>

          {!isNew && (
            <div style={{ marginBottom: 10 }}>
              {loadingAttachments && <div className="text-secondary" style={{ fontSize: 12.5 }}>불러오는 중...</div>}
              {!loadingAttachments && attachError && <div className="text-danger" style={{ fontSize: 12.5 }}>{attachError}</div>}
              {!loadingAttachments && !attachError && existingAttachments.length > 0 && (
                <ul className="file-list" style={{ marginBottom: 10 }}>
                  {existingAttachments.map((a) => (
                    <li key={a.attachId} className="file-list-item">
                      <span className="file-ext">{(a.originalName.split('.').pop() || '').slice(0, 4).toUpperCase()}</span>
                      <button
                        type="button"
                        className="file-name"
                        style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', color: 'var(--color-accent)', cursor: 'pointer' }}
                        onClick={() => downloadNoticeAttachment(a.attachId, a.originalName).catch((err) => window.alert(err.response?.data?.message || '파일 다운로드에 실패했습니다.'))}
                        title="다운로드"
                      >
                        {a.originalName}
                      </button>
                      <span className="file-size">{formatDateTime(a.createdAt)}</span>
                      <button type="button" className="file-remove" onClick={() => setRemoveTarget(a)} aria-label={`${a.originalName} 삭제`}>&times;</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <MultiFileUpload files={pendingFiles} onChange={setPendingFiles} maxFiles={10} maxSizeMB={20} />
        </div>
      </form>

      {removeTarget && (
        <ConfirmDialog
          title="첨부파일 삭제"
          message={`'${removeTarget.originalName}' 파일을 삭제하시겠습니까?`}
          confirmLabel="삭제"
          danger
          onCancel={() => setRemoveTarget(null)}
          onConfirm={handleRemoveExisting}
        />
      )}
    </Modal>
  );
}
